import { useState, useMemo, useEffect, useRef } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getISOWeek, getDay } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import type { Trade, TradingRoutine, RoutineCompletion, TradingGoal } from "wasp/entities";
import { netPnl } from "../trading-journal/analytics";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader, useIsDark } from "../client/components/ui/card-section-header";
import { Button } from "../client/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle, AlertCircle, Settings } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { useLanguageContext } from "../client/contexts/LanguageContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../client/components/ui/tooltip";
import { HOLIDAYS_BY_DATE } from './marketHolidays';
import { tradeDayKey } from './calendarDayKey';
import { useTradingDayStart } from './useTradingDayStart';

export interface SessionWithErrors {
  startedAt: Date | string | null;
  errors?: { id: string; name: string }[];
}

export interface AccountEvent {
  date: Date;
  accountName: string;
  type: 'created' | 'crashed';
}

export interface FundedEvent {
  date: Date;
  accountName: string;
}

export interface PayoutEvent {
  date: Date;
  accountName: string;
  amount: number;
  currency: string;
}

interface AccountInfo {
  id: string;
  name: string;
  groupId: string | null;
}

interface GroupInfo {
  id: string;
  name: string;
}

export type DayViolationSeverity = "violation" | "warning";

export interface DayViolationSummary {
  severity: DayViolationSeverity;
  hasDailyLoss: boolean;
  hasConsistency: boolean;
  tooltips: string[];
}

interface TradingCalendarProps {
  trades: Trade[];
  routines: (TradingRoutine & { routineCompletions: RoutineCompletion[] })[];
  goals: TradingGoal[];
  accounts?: AccountInfo[];
  groups?: GroupInfo[];
  accountEvents?: AccountEvent[];
  fundedEvents?: FundedEvent[];
  payoutEvents?: PayoutEvent[];
  sessions?: SessionWithErrors[];
  /** Map from "yyyy-MM-dd" to aggregated violation summary across all accounts */
  violationsByDate?: Map<string, DayViolationSummary>;
  onMonthChange?: (date: Date) => void;
  onDayClick?: (date: Date) => void;
  className?: string;
  /** `lite` : en-tête des cartes du mode Lite (titre, sous-titre) au lieu de CardSectionHeader. */
  variant?: "default" | "lite";
  title?: string;
  subtitle?: string;
}

interface DayData {
  pnl: number | null;
  tradeCount: number;
  routinesTotal: number;
  routinesDone: number;
  goalDeadlines: string[];
  accountIds: Set<string>;
  accountEvents: AccountEvent[];
  fundedEvents: FundedEvent[];
  payoutEvents: PayoutEvent[];
}

function getAccountLabels(accountIds: Set<string>, accounts: AccountInfo[], groups: GroupInfo[]): string[] {
  if (accountIds.size === 0) return [];

  // Collect group ids that are represented
  const groupIds = new Set<string>();
  const ungroupedNames: string[] = [];

  for (const accountId of accountIds) {
    const account = accounts.find((a) => a.id === accountId);
    if (!account) continue;
    if (account.groupId && groups.some((g) => g.id === account.groupId)) {
      groupIds.add(account.groupId);
    } else {
      ungroupedNames.push(account.name);
    }
  }

  const groupNames = [...groupIds].map((gid) => groups.find((g) => g.id === gid)?.name ?? "").filter(Boolean);

  // If there are any groups, show group names + ungrouped account names
  // If no groups at all, show account names
  return [...groupNames, ...ungroupedNames];
}

export default function TradingCalendar({ trades, routines, goals, accounts = [], groups = [], accountEvents = [], fundedEvents, payoutEvents, sessions = [], violationsByDate, onMonthChange, onDayClick, className, variant = "default", title, subtitle }: TradingCalendarProps) {
  const t = useTranslation();
  const { language } = useLanguageContext();
  const isDark = useIsDark();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hideWeekend, setHideWeekend] = useState(() => localStorage.getItem("calendar_hide_weekend") === "true");
  const [showErrors, setShowErrors] = useState(() => localStorage.getItem("calendar_show_errors") === "true");
  const [tradingDayStartUtcHour, setTradingDayStartUtcHour] = useTradingDayStart();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [settingsOpen]);

  // Responsive sizing based on container height
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setContainerHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // xs < 320px height, sm < 420px, md < 560px, lg >= 560px
  const calSize: "xs" | "sm" | "md" | "lg" =
    containerHeight > 0 && containerHeight < 320 ? "xs" :
    containerHeight > 0 && containerHeight < 420 ? "sm" :
    containerHeight > 0 && containerHeight < 560 ? "md" : "lg";
  const isXs = calSize === "xs";
  const isSm = calSize === "sm" || isXs;

  const errorsByDate = useMemo(() => {
    const map = new Map<string, string[]>();
    sessions.forEach((session) => {
      if (!session.startedAt || !session.errors || session.errors.length === 0) return;
      const d = new Date(session.startedAt);
      if (isNaN(d.getTime())) return;
      d.setHours(0, 0, 0, 0);
      const key = format(d, "yyyy-MM-dd");
      const existing = map.get(key) ?? [];
      session.errors.forEach((err) => {
        if (!existing.includes(err.name)) existing.push(err.name);
      });
      map.set(key, existing);
    });
    return map;
  }, [sessions]);

  const locale = language === "fr" ? fr : enUS;

  useEffect(() => {
    onMonthChange?.(currentDate);
  }, []);

  const activeRoutines = useMemo(() => routines.filter((r) => r.isActive), [routines]);
  const goalsWithDeadlines = useMemo(() => goals.filter((g) => !g.isCompleted && g.deadline), [goals]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { locale });
    const calendarEnd = endOfWeek(monthEnd, { locale });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate, locale]);

  // Aggregate data per day
  const dataByDate = useMemo(() => {
    const map = new Map<string, DayData>();

    const getOrCreate = (key: string): DayData => {
      if (!map.has(key)) {
        map.set(key, { pnl: null, tradeCount: 0, routinesTotal: 0, routinesDone: 0, goalDeadlines: [], accountIds: new Set(), accountEvents: [], fundedEvents: [], payoutEvents: [] });
      }
      return map.get(key)!;
    };

    // Trades → P&L journalier
    // The trading day starts at `tradingDayStartUtcHour` (UTC). A trade at or
    // after that hour belongs to its own calendar day; a trade before that hour
    // is the tail of the previous session and rolls back to the previous day.
    // Keyed by UTC calendar date so attribution is timezone-independent and
    // aligns with the grid cells. See tradeDayKey() for the math + tests.
    trades.forEach((trade) => {
      const key = tradeDayKey(trade.date, tradingDayStartUtcHour);
      const day = getOrCreate(key);
      day.tradeCount += 1;
      if (trade.pnl !== null && trade.pnl !== undefined) {
        day.pnl = (day.pnl ?? 0) + netPnl(trade);
      }
      const accountId = (trade as any).accountId as string | null;
      if (accountId) {
        day.accountIds.add(accountId);
      }
    });

    // Routines
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    activeRoutines.forEach((routine) => {
      // Completions existantes
      routine.routineCompletions.forEach((completion) => {
        const d = new Date(completion.date);
        d.setHours(0, 0, 0, 0);
        const key = format(d, "yyyy-MM-dd");
        const day = getOrCreate(key);
        day.routinesTotal += 1;
        const allDone = completion.completedItems.length === routine.checklistItems.length;
        if (allDone) day.routinesDone += 1;
      });

      // Jours passés sans completion pour les routines quotidiennes
      if (routine.frequency === "daily") {
        calendarDays.forEach((day) => {
          if (!isSameMonth(day, currentDate) || day > today) return;
          const key = format(day, "yyyy-MM-dd");
          const hasCompletion = routine.routineCompletions.some((c) => {
            const cd = new Date(c.date);
            cd.setHours(0, 0, 0, 0);
            return format(cd, "yyyy-MM-dd") === key;
          });
          if (!hasCompletion) {
            const dayData = getOrCreate(key);
            dayData.routinesTotal += 1;
          }
        });
      }
    });

    // Goals deadlines
    goalsWithDeadlines.forEach((goal) => {
      if (!goal.deadline) return;
      const d = new Date(goal.deadline);
      d.setHours(0, 0, 0, 0);
      const key = format(d, "yyyy-MM-dd");
      const day = getOrCreate(key);
      day.goalDeadlines.push(goal.title);
    });

    // Account events (creation / crash)
    (accountEvents ?? []).forEach((event) => {
      const d = new Date(event.date);
      d.setHours(0, 0, 0, 0);
      const key = format(d, "yyyy-MM-dd");
      const day = getOrCreate(key);
      day.accountEvents.push(event);
    });

    // Funded transitions
    (fundedEvents ?? []).forEach((event) => {
      const d = new Date(event.date);
      d.setHours(0, 0, 0, 0);
      const key = format(d, "yyyy-MM-dd");
      const day = getOrCreate(key);
      day.fundedEvents.push(event);
    });

    // Payouts
    (payoutEvents ?? []).forEach((event) => {
      const d = new Date(event.date);
      d.setHours(0, 0, 0, 0);
      const key = format(d, "yyyy-MM-dd");
      const day = getOrCreate(key);
      day.payoutEvents.push(event);
    });

    return map;
  }, [trades, activeRoutines, goalsWithDeadlines, calendarDays, currentDate, accountEvents, fundedEvents, payoutEvents, tradingDayStartUtcHour]);

  const previousMonth = () => {
    const newDate = subMonths(currentDate, 1);
    setCurrentDate(newDate);
    onMonthChange?.(newDate);
  };

  const nextMonth = () => {
    const newDate = addMonths(currentDate, 1);
    setCurrentDate(newDate);
    onMonthChange?.(newDate);
  };

  const weekDays = useMemo(() => {
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(calendarDays[0]);
      day.setDate(day.getDate() + i);
      days.push(format(day, "EEE", { locale }));
    }
    return days;
  }, [calendarDays, locale]);

  // Grouper les jours par semaine (lignes de 7)
  const weeks = useMemo(() => {
    const result: { days: Date[]; weekPnl: number | null; weekTrades: number }[] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      const days = calendarDays.slice(i, i + 7);
      let weekPnl: number | null = null;
      let weekTrades = 0;
      days.forEach((day) => {
        const key = format(day, "yyyy-MM-dd");
        const data = dataByDate.get(key);
        if (data && isSameMonth(day, currentDate)) {
          weekTrades += data.tradeCount;
          if (data.pnl !== null) {
            weekPnl = (weekPnl ?? 0) + data.pnl;
          }
        }
      });
      result.push({ days, weekPnl, weekTrades });
    }
    return result;
  }, [calendarDays, dataByDate, currentDate]);

  const formatPnl = (pnl: number) => {
    const sign = pnl >= 0 ? "+" : "";
    if (Math.abs(pnl) >= 1000) {
      return `${sign}${(pnl / 1000).toFixed(1)}k$`;
    }
    return `${sign}${pnl.toFixed(2)}$`;
  };

  const calendarControls = (
          <div className="flex items-center gap-2">
            {/* Settings button + animated dropdown */}
            <div ref={settingsRef} className="relative">
              <Button
                variant={settingsOpen ? "default" : "outline"}
                size="sm"
                onClick={() => setSettingsOpen((v) => !v)}
                title={t.dashboard.calendar.calendarSettings}
              >
                <Settings className={`h-4 w-4 transition-transform duration-300 ${settingsOpen ? "rotate-90" : "rotate-0"}`} />
              </Button>
              <div
                className={`absolute right-0 top-full mt-2 z-50 w-72 rounded-xl border shadow-xl overflow-hidden transition-all duration-200 origin-top-right ${
                  settingsOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-1 pointer-events-none"
                } ${isDark ? "bg-card border-border/60" : "bg-white border-border/40"}`}
              >
                <div className={`px-4 py-2.5 border-b text-xs font-semibold tracking-wide uppercase text-muted-foreground ${isDark ? "border-border/40" : "border-border/30"}`}>
                  {t.dashboard.calendar.calendarSettings}
                </div>
                <div className="p-3 flex flex-col gap-3">
                  {/* Toggle: weekends */}
                  <label className="flex items-center justify-between gap-3 cursor-pointer group">
                    <span className="text-sm text-foreground/90 group-hover:text-foreground transition-colors">
                      {t.dashboard.calendar.settingsWeekend}
                    </span>
                    <button
                      role="switch"
                      aria-checked={!hideWeekend}
                      onClick={() => {
                        const next = !hideWeekend;
                        setHideWeekend(next);
                        localStorage.setItem("calendar_hide_weekend", String(next));
                      }}
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        !hideWeekend ? "bg-primary" : isDark ? "bg-muted" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                          !hideWeekend ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>

                  {/* Toggle: errors */}
                  <label className="flex items-center justify-between gap-3 cursor-pointer group">
                    <span className="text-sm text-foreground/90 group-hover:text-foreground transition-colors">
                      {t.dashboard.calendar.settingsErrors}
                    </span>
                    <button
                      role="switch"
                      aria-checked={showErrors}
                      onClick={() => {
                        const next = !showErrors;
                        setShowErrors(next);
                        localStorage.setItem("calendar_show_errors", String(next));
                      }}
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        showErrors ? "bg-primary" : isDark ? "bg-muted" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${
                          showErrors ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>

                  {/* Trading day start hour */}
                  <div className={`pt-1 border-t ${isDark ? "border-border/40" : "border-border/30"}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-sm text-foreground/90">{t.dashboard.calendar.settingsTradingDayStart}</span>
                      <span className="text-sm font-mono font-semibold text-primary">
                        {tradingDayStartUtcHour === 0 ? "0h UTC" : `${tradingDayStartUtcHour}h UTC`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{t.dashboard.calendar.settingsTradingDayStartDesc}</p>
                    <input
                      type="range"
                      min={0}
                      max={23}
                      step={1}
                      value={tradingDayStartUtcHour}
                      onChange={(e) => {
                        setTradingDayStartUtcHour(parseInt(e.target.value, 10));
                      }}
                      className="w-full h-1.5 accent-primary cursor-pointer rounded-full"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                      <span>0h</span>
                      <span>6h</span>
                      <span>12h</span>
                      <span>18h</span>
                      <span>23h</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Button variant="outline" size="sm" onClick={previousMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[150px] text-center">
              {format(currentDate, "MMMM yyyy", { locale })}
            </span>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
  );

  return (
    <Card ref={containerRef} className={`${className ?? ""} h-full flex flex-col overflow-hidden`}>
      {variant === "lite" ? (
        // En-tête des cartes du mode Lite (titre + sous-titre), à la place de la
        // barre CardSectionHeader de l'Advanced. Mêmes réglages, même navigation.
        <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground">{title ?? t.analytics.calendarLabel}</h2>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {calendarControls}
        </header>
      ) : (
        <CardSectionHeader
          label={t.analytics.calendarLabel}
          isDark={isDark}
          compact
          rightContent={calendarControls}
        />
      )}
      <CardContent className={`${isSm ? "px-2 pb-1 pt-0.5" : "px-3 pb-2 pt-1"} flex-1 overflow-hidden flex flex-col min-h-0`}>
        <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Week day headers */}
          <div className={`grid gap-0.5 ${isSm ? "mb-0.5" : "mb-1"} ${hideWeekend ? "grid-cols-5 sm:grid-cols-6" : "grid-cols-7 sm:grid-cols-8"}`}>
            {calendarDays.slice(0, 7).map((day, index) => {
              if (hideWeekend && (getDay(day) === 0 || getDay(day) === 6)) return null;
              const label = format(day, "EEE", { locale });
              return (
                <div key={index} className={`text-center font-semibold text-muted-foreground py-0.5 ${isSm ? "text-[9px]" : "text-xs"}`}>
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{label.charAt(0)}</span>
                </div>
              );
            })}
            <div className="hidden sm:block text-center text-xs font-semibold text-muted-foreground/60 py-0.5">
              {t.dashboard.calendar.week}
            </div>
          </div>

          {/* Calendar grid — une ligne par semaine */}
          <div
            className="flex-1 min-h-0 grid gap-0.5"
            style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))` }}
          >
            {weeks.map((week, weekIndex) => {
              const weekPnlColor = week.weekPnl === null
                ? "text-muted-foreground/40"
                : week.weekPnl > 0
                ? "text-pnlPositive"
                : week.weekPnl < 0
                ? "text-pnlNegative"
                : "text-yellow-500";

              return (
                <div key={weekIndex} className={`grid gap-0.5 ${hideWeekend ? "grid-cols-5 sm:grid-cols-6" : "grid-cols-7 sm:grid-cols-8"}`}>
                  {/* Jours (5 ou 7 selon hideWeekend) */}
                  {week.days.map((day, dayIndex) => {
                    if (hideWeekend && (getDay(day) === 0 || getDay(day) === 6)) return null;
                    const dayKey = format(day, "yyyy-MM-dd");
                    const data = dataByDate.get(dayKey);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());

                    const hasTrades = data && data.tradeCount > 0;
                    const pnl = data?.pnl ?? null;
                    const isProfit = pnl !== null && pnl > 0;
                    const isLoss = pnl !== null && pnl < 0;
                    const isBreakeven = pnl !== null && pnl === 0;

                    let bgClass = isCurrentMonth ? "bg-card" : "bg-muted/20";
                    if (hasTrades) {
                      if (isProfit) bgClass = isDark
                        ? (isCurrentMonth ? "bg-pnlPositive/10" : "bg-pnlPositive/5")
                        : (isCurrentMonth ? "bg-pnlPositive/10" : "bg-pnlPositive/5");
                      else if (isLoss) bgClass = isDark
                        ? (isCurrentMonth ? "bg-pnlNegative/10" : "bg-pnlNegative/5")
                        : (isCurrentMonth ? "bg-pnlNegative/10" : "bg-pnlNegative/5");
                      else if (isBreakeven) bgClass = isDark
                        ? (isCurrentMonth ? "bg-yellow-950/40" : "bg-yellow-950/20")
                        : (isCurrentMonth ? "bg-yellow-50" : "bg-yellow-50/40");
                    }

                    const pnlColor = isProfit
                      ? (isCurrentMonth ? "text-pnlPositive" : "text-pnlPositive/50")
                      : isLoss
                      ? (isCurrentMonth ? "text-pnlNegative" : "text-pnlNegative/50")
                      : isBreakeven
                      ? (isCurrentMonth ? "text-yellow-500" : "text-yellow-500/50")
                      : "text-muted-foreground";

                    let borderClass = isCurrentMonth ? "border border-border/50" : "border border-border/25";
                    if (hasTrades) {
                      if (isProfit) borderClass = isCurrentMonth
                        ? "border border-pnlPositive/40 border-l-2 border-l-pnlPositive"
                        : "border border-pnlPositive/20 border-l-2 border-l-pnlPositive/40";
                      else if (isLoss) borderClass = isCurrentMonth
                        ? "border border-pnlNegative/40 border-l-2 border-l-pnlNegative"
                        : "border border-pnlNegative/20 border-l-2 border-l-pnlNegative/40";
                      else if (isBreakeven) borderClass = isCurrentMonth
                        ? "border border-yellow-500/40 border-l-2 border-l-yellow-500"
                        : "border border-yellow-500/20 border-l-2 border-l-yellow-500/40";
                    }

                    const routinesTotal = data?.routinesTotal ?? 0;
                    const routinesDone = data?.routinesDone ?? 0;
                    const goalDeadlines = data?.goalDeadlines ?? [];

                    const dayViolation = isCurrentMonth ? violationsByDate?.get(dayKey) : undefined;

                    const dayHolidays = isCurrentMonth ? (HOLIDAYS_BY_DATE.get(dayKey) ?? []) : [];
                    const usHolidays = dayHolidays.filter(h => h.market === 'US');
                    const euHolidays = dayHolidays.filter(h => h.market === 'EU');
                    const frHolidays = dayHolidays.filter(h => h.market === 'FR');

                    const holidayBadges = (usHolidays.length > 0 || euHolidays.length > 0 || frHolidays.length > 0) ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex gap-0.5 items-center">
                          {usHolidays.length > 0 && (
                            <span className="text-[9px] leading-none" title={`🇺🇸 NYSE - ${usHolidays.map(h => h.label).join(', ')}`}>🇺🇸</span>
                          )}
                          {euHolidays.length > 0 && (
                            <span className="text-[9px] leading-none" title={`🇪🇺 Euronext - ${euHolidays.map(h => h.label).join(', ')}`}>🇪🇺</span>
                          )}
                          {frHolidays.length > 0 && (
                            <span className="text-[9px] leading-none" title={`🇫🇷 ${frHolidays.map(h => h.label).join(', ')}`}>🇫🇷</span>
                          )}
                        </div>
                        <span className="hidden sm:block text-[8px] leading-none text-muted-foreground/60">
                          {[
                            usHolidays.length > 0 ? `NYSE ${t.dashboard.calendar.marketClosed}` : null,
                            euHolidays.length > 0 ? `Euronext ${t.dashboard.calendar.marketClosed}` : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    ) : null;

                    const dayErrors = (showErrors && isCurrentMonth) ? (errorsByDate.get(dayKey) ?? []) : [];

                    const isClickable = (isCurrentMonth || hasTrades) && !!onDayClick;

                    return (
                      <div
                        key={dayIndex}
                        onClick={() => isClickable && onDayClick?.(day)}
                        className={`min-h-0 rounded-md ${isSm ? "p-0.5" : "p-1"} flex flex-col overflow-hidden ${bgClass} ${borderClass} ${
                          isToday ? "ring-2 ring-inset ring-primary z-10 relative" : ""
                        }${isClickable ? " cursor-pointer hover:ring-1 hover:ring-primary/40 transition-shadow" : ""}${
                          !isCurrentMonth ? " opacity-60" : ""
                        }`}
                      >
                        <div className={`flex items-center justify-between ${isSm ? "mb-0.5" : "mb-1"}`}>
                          <span
                            className={`${isSm ? "text-[9px]" : "text-xs"} font-semibold leading-none ${
                              !isCurrentMonth
                                ? "text-muted-foreground/40"
                                : isToday
                                ? "text-primary"
                                : "text-foreground/70"
                            }`}
                          >
                            {format(day, "d")}
                          </span>
                          {isCurrentMonth && !isXs && (routinesTotal > 0 || goalDeadlines.length > 0 || (data?.accountEvents?.length ?? 0) > 0 || (data?.fundedEvents?.length ?? 0) > 0 || (data?.payoutEvents?.length ?? 0) > 0 || dayViolation) && (
                            <div className="flex items-center gap-0.5">
                              {dayViolation && (
                                <TooltipProvider delayDuration={200}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="shrink-0 cursor-default">
                                        {dayViolation.severity === "violation"
                                          ? <AlertCircle className="w-3 h-3 text-red-500" />
                                          : <AlertTriangle className="w-3 h-3 text-yellow-500" />
                                        }
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-[220px]">
                                      <ul className="space-y-0.5">
                                        {dayViolation.tooltips.map((label, i) => (
                                          <li key={i}>{label}</li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {routinesTotal > 0 && (
                                <span
                                  title={`${routinesDone}/${routinesTotal} ${t.dashboard.calendar.routinesCompleted}`}
                                  className={`inline-block w-2 h-2 rounded-full ${
                                    routinesDone === routinesTotal && routinesTotal > 0
                                      ? "bg-blue-500"
                                      : routinesDone > 0
                                      ? "bg-blue-300"
                                      : "bg-muted-foreground/30"
                                  }`}
                                />
                              )}
                              {goalDeadlines.length > 0 && (
                                <span
                                  title={`${t.dashboard.calendar.goalDeadline}: ${goalDeadlines.join(", ")}`}
                                  className="inline-block w-2 h-2 rounded-full bg-orange-500"
                                />
                              )}
                              {(() => {
                                const events = data?.accountEvents ?? [];
                                const created = events.filter(e => e.type === 'created');
                                const crashed = events.filter(e => e.type === 'crashed');
                                const createdNames = created.map(e => e.accountName).join(', ');
                                const crashedNames = crashed.map(e => e.accountName).join(', ');
                                return (
                                  <>
                                    {created.length >= 5 ? (
                                      <span
                                        title={`${createdNames} - ${t.dashboard.calendar.accountCreated}`}
                                        className="text-[11px] leading-none flex-shrink-0 cursor-default"
                                      >⭐</span>
                                    ) : created.map((e, i) => (
                                      <span
                                        key={`created-${i}`}
                                        title={`${e.accountName} - ${t.dashboard.calendar.accountCreated}`}
                                        className="inline-block w-2 h-2 rounded-full bg-success flex-shrink-0"
                                      />
                                    ))}
                                    {crashed.length >= 5 ? (
                                      <span
                                        title={`${crashedNames} - ${t.dashboard.calendar.accountCrashed}`}
                                        className="text-[11px] leading-none flex-shrink-0 cursor-default"
                                      >🍌</span>
                                    ) : crashed.map((e, i) => (
                                      <span
                                        key={`crashed-${i}`}
                                        title={`${e.accountName} - ${t.dashboard.calendar.accountCrashed}`}
                                        className="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0"
                                      />
                                    ))}
                                  </>
                                );
                              })()}
                              {/* Funded emoji 🏆 */}
                              {(() => {
                                const funded = data?.fundedEvents ?? [];
                                if (funded.length === 0) return null;
                                const names = funded.map(e => e.accountName).join(', ');
                                const label = funded.length === 1
                                  ? `${names} - ${t.dashboard.calendar.accountFunded}`
                                  : `${names} - ${t.dashboard.calendar.accountFundedMany}`;
                                return (
                                  <span
                                    title={label}
                                    className="text-[11px] leading-none flex-shrink-0 cursor-default"
                                  >🏆</span>
                                );
                              })()}
                              {/* Payout emoji 💰 */}
                              {(() => {
                                const payouts = data?.payoutEvents ?? [];
                                if (payouts.length === 0) return null;
                                const label = payouts.length === 1
                                  ? `${payouts[0].accountName} - ${t.dashboard.calendar.calendarPayout}`
                                  : `${payouts.map(e => e.accountName).join(', ')} - ${t.dashboard.calendar.calendarPayoutMany}`;
                                return (
                                  <span
                                    title={label}
                                    className="text-[11px] leading-none flex-shrink-0 cursor-default"
                                  >💰</span>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        {dayErrors.length > 0 && (
                          <div
                            className="flex flex-wrap gap-0.5 mb-1"
                            title={`${t.dashboard.calendar.errorsOnDay}: ${dayErrors.join(", ")}`}
                          >
                            {dayErrors.map((name) => (
                              <span
                                key={name}
                                className="inline-flex items-center rounded-full px-1.5 py-0 text-[9px] leading-tight font-medium bg-red-500/15 text-red-600 dark:text-red-300 border border-red-500/30 truncate max-w-full"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        )}

                        {hasTrades ? (
                          <div className="flex flex-col gap-0.5 mt-auto min-w-0">
                            <span className={`${isSm ? "text-[9px]" : "text-[10px] sm:text-sm"} font-bold leading-tight truncate ${pnlColor}`}>
                              {pnl !== null ? formatPnl(pnl) : "-"}
                            </span>
                            {!isSm && <span className="hidden sm:block text-[10px] text-muted-foreground leading-none">
                              {data!.tradeCount} {t.dashboard.calendar.trades}
                            </span>}
                            {isCurrentMonth && !isSm && (() => {
                              const labels = getAccountLabels(data!.accountIds, accounts, groups);
                              if (labels.length === 0 && data!.accountIds.size > 0) {
                                return <span className="text-[9px] text-yellow-500">{data!.accountIds.size} acct(s)</span>;
                              }
                              return labels.map((label) => (
                                <span key={label} className="text-[9px] leading-none px-1 py-0.5 rounded bg-primary/10 text-primary font-medium truncate">
                                  {label}
                                </span>
                              ));
                            })()}
                            {isCurrentMonth && !isSm && holidayBadges}
                          </div>
                        ) : isCurrentMonth ? (
                          <div className="mt-auto flex flex-col gap-0.5">
                            {!isSm && <span className="hidden sm:block text-[10px] text-muted-foreground/40">{t.dashboard.calendar.noTrades}</span>}
                            {holidayBadges}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}

                  {/* Colonne récap hebdo — masquée sur mobile */}
                  <div className={`hidden sm:flex min-h-0 rounded-md ${isSm ? "p-0.5" : "p-1.5"} flex-col justify-center items-center border ${
                    isDark ? "bg-muted/10 border-border/30" : "bg-muted/20 border-border/30"
                  }`}>
                    {week.weekTrades > 0 ? (
                      <>
                        <span className={`${isSm ? "text-[9px]" : "text-sm"} font-bold leading-tight text-center ${weekPnlColor}`}>
                          {week.weekPnl !== null ? formatPnl(week.weekPnl) : "-"}
                        </span>
                        {!isXs && <span className="text-[10px] text-muted-foreground/60 mt-1 text-center">
                          {week.weekTrades} {t.dashboard.calendar.trades}
                        </span>}
                      </>
                    ) : (
                      <span className="text-[10px] text-muted-foreground/30">-</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          {(() => {
            const monthKeys = calendarDays
              .filter(d => isSameMonth(d, currentDate))
              .map(d => format(d, "yyyy-MM-dd"));
            const totalCreated = monthKeys.reduce((sum, k) => sum + (dataByDate.get(k)?.accountEvents ?? []).filter(e => e.type === 'created').length, 0);
            const totalCrashed = monthKeys.reduce((sum, k) => sum + (dataByDate.get(k)?.accountEvents ?? []).filter(e => e.type === 'crashed').length, 0);
            const hasAccountCreated = totalCreated > 0;
            const hasAccountCrashed = totalCrashed > 0;
            const hasFundedEvents = monthKeys.some(k => (dataByDate.get(k)?.fundedEvents.length ?? 0) > 0);
            const hasPayoutEvents = monthKeys.some(k => (dataByDate.get(k)?.payoutEvents.length ?? 0) > 0);
            const hasCreatedEmoji = monthKeys.some(k => {
              const events = dataByDate.get(k)?.accountEvents ?? [];
              return events.filter(e => e.type === 'created').length >= 5;
            });
            const hasCrashedEmoji = monthKeys.some(k => {
              const events = dataByDate.get(k)?.accountEvents ?? [];
              return events.filter(e => e.type === 'crashed').length >= 5;
            });
            const hasRoutines = monthKeys.some(k => (dataByDate.get(k)?.routinesTotal ?? 0) > 0);
            const hasGoalDeadlines = monthKeys.some(k => (dataByDate.get(k)?.goalDeadlines.length ?? 0) > 0);
            const hasUsHolidays = monthKeys.some(k => (HOLIDAYS_BY_DATE.get(k) ?? []).some(h => h.market === 'US'));
            const hasEuHolidays = monthKeys.some(k => (HOLIDAYS_BY_DATE.get(k) ?? []).some(h => h.market === 'EU'));
            const hasFrHolidays = monthKeys.some(k => (HOLIDAYS_BY_DATE.get(k) ?? []).some(h => h.market === 'FR'));
            return (
              <div className={`${isXs ? "hidden" : ""} mt-1 flex-none flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground`}>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-pnlPositive/10 border border-pnlPositive/40 border-l-2 border-l-pnlPositive" />
                  <span>{t.dashboard.calendar.profitDay}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-pnlNegative/10 border border-pnlNegative/40 border-l-2 border-l-pnlNegative" />
                  <span>{t.dashboard.calendar.lossDay}</span>
                </div>
                {hasRoutines && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span>{t.dashboard.calendar.routine} {t.dashboard.calendar.completed}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/30" />
                      <span>{t.dashboard.calendar.routine} {t.dashboard.calendar.notCompleted}</span>
                    </div>
                  </>
                )}
                {hasGoalDeadlines && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span>{t.dashboard.calendar.goalDeadline}</span>
                  </div>
                )}
                {hasAccountCreated && (
                  <div className="flex items-center gap-1.5">
                    {hasCreatedEmoji
                      ? <span className="text-[11px] leading-none">⭐</span>
                      : <div className="w-2 h-2 rounded-full bg-success" />
                    }
                    <span>{hasCreatedEmoji ? t.dashboard.calendar.accountCreatedMany : t.dashboard.calendar.accountCreated}</span>
                  </div>
                )}
                {hasAccountCrashed && (
                  <div className="flex items-center gap-1.5">
                    {hasCrashedEmoji
                      ? <span className="text-[11px] leading-none">🍌</span>
                      : <div className="w-2 h-2 rounded-full bg-red-500" />
                    }
                    <span>{hasCrashedEmoji ? t.dashboard.calendar.accountCrashedMany : t.dashboard.calendar.accountCrashed}</span>
                  </div>
                )}
                {hasFundedEvents && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] leading-none">🏆</span>
                    <span>{t.dashboard.calendar.accountFunded}</span>
                  </div>
                )}
                {hasPayoutEvents && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] leading-none">💰</span>
                    <span>{t.dashboard.calendar.calendarPayout}</span>
                  </div>
                )}
                {hasUsHolidays && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] leading-none">🇺🇸</span>
                    <span>{t.dashboard.calendar.usMarketClosed}</span>
                  </div>
                )}
                {hasEuHolidays && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] leading-none">🇪🇺</span>
                    <span>{t.dashboard.calendar.euMarketClosed}</span>
                  </div>
                )}
                {hasFrHolidays && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] leading-none">🇫🇷</span>
                    <span>{t.dashboard.calendar.frPublicHoliday}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
}
